import { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { addNotification, setConnectionStatus } from '../store/slices/notificationsSlice';
import { addMeeting, updateMeeting, removeMeeting } from '../store/slices/meetingsSlice';
import toast from 'react-hot-toast';

// WebSocket connection states
export const WS_STATES = {
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  RECONNECTING: 'reconnecting',
  ERROR: 'error'
};

// Exponential backoff configuration
const RECONNECT_CONFIG = {
  INITIAL_DELAY: 1000,     // 1 second
  MAX_DELAY: 30000,        // 30 seconds
  BACKOFF_FACTOR: 2,       // Double delay each time
  MAX_RETRIES: 10          // Maximum retry attempts
};

const useWebSocket = (url = null) => {
  const dispatch = useDispatch();
  const { tokens } = useSelector(state => state.auth);
  
  // State management
  const [connectionState, setConnectionState] = useState(WS_STATES.DISCONNECTED);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  
  // Refs for managing WebSocket connection and reconnection
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectDelayRef = useRef(RECONNECT_CONFIG.INITIAL_DELAY);
  const mountedRef = useRef(true);
  
  // Toast state management to prevent duplicates
  const hasShownConnectionErrorRef = useRef(false);
  const hasShownMaxRetriesErrorRef = useRef(false);

  console.log("🏗️ useWebSocket hook initialized, mountedRef:", mountedRef.current);

  // Ensure mountedRef is properly managed
  useEffect(() => {
    mountedRef.current = true;
    console.log("✅ Component mounted, mountedRef set to true");
    
    return () => {
      console.log("🧹 Component unmounting, setting mountedRef to false");
      mountedRef.current = false;
    };
  }, []);

  // WebSocket URL with JWT token - use environment variable and proper protocol
  const getWebSocketUrl = () => {
    if (url) return url;
    
    const apiEndpoint = process.env.REACT_APP_API_ENDPOINT || 'http://localhost:8000';
    // Convert HTTP(S) to WS(S)
    const wsEndpoint = apiEndpoint.replace(/^https?:\/\//, (match) => 
      match === 'https://' ? 'wss://' : 'ws://'
    );
    
    const wsUrl = `${wsEndpoint}/ws/notifications/?token=${tokens?.access}`;
    console.log('🔗 WebSocket URL:', wsUrl);
    return wsUrl;
  };

  const wsUrl = getWebSocketUrl();

  // Update connection state and dispatch to Redux
  const updateConnectionState = useCallback((state) => {
    console.log("📊 mountedRef.current:", mountedRef.current);
    console.log("📊 updateConnectionState called with:", state);
    
    if (!mountedRef.current) {
      console.log("⚠️ Component unmounted, skipping state update");
      return;
    }

    console.log("✅ Component mounted, proceeding with state update");
    console.log("setConnectionStatus setting in update", state);
    setConnectionState(state);
    dispatch(setConnectionStatus(state));
    console.log("📊 State update completed");
  }, [dispatch]);

  // Reset toast flags when connection is successful
  const resetToastFlags = useCallback(() => {
    hasShownConnectionErrorRef.current = false;
    hasShownMaxRetriesErrorRef.current = false;
  }, []);

  // Normalize different backend payload shapes into a consistent object
  const normalizeNotification = useCallback((raw) => {
    if (!raw || typeof raw !== 'object') return null;

    const normalized = {
      // Support id as string or number
      id: raw.id || raw.pk || raw.uuid || raw.notification_id,
      // Prefer text but fallback to message or content
      text: raw.text || raw.message || raw.content,
      // Support various timestamp keys
      created_at: raw.created_at || raw.createdAt || raw.timestamp || raw.created,
      // Preserve any other fields
      ...raw
    };
    return normalized;
  }, []);

  // Validate incoming notification message
  const validateNotificationMessage = useCallback((data) => {
    if (!data || typeof data !== 'object') return false;

    if (!data.id || !data.text || !data.created_at) {
      return false;
    }

    // text should be string; id can be string or number
    if (typeof data.text !== 'string') return false;

    return true;
  }, []);

  // Handle incoming WebSocket messages
  const handleMessage = useCallback((event) => {
    try {
      const raw = JSON.parse(event.data);
      const data = normalizeNotification(raw);

      if (validateNotificationMessage(data)) {
        try {
          dispatch(addNotification(data));
          toast.success(data.text, {
            duration: 4000,
            position: 'top-right',
            style: { background: '#10B981', color: '#fff' },
          });
          console.log('📢 New notification received:', data);
        } catch (dispatchError) {
          console.error('Error dispatching notification to Redux:', dispatchError);
        }
      } else {
        console.warn('Ignoring unknown notification payload:', raw);
      }

      // === Meetings real-time sync ===
      if (raw?.category === 'meetings') {
        const { notification_type, meeting } = raw;
        if (notification_type === 'meeting_created' && meeting) {
          dispatch(addMeeting(meeting));
        }
        if (notification_type === 'meeting_updated' && meeting) {
          dispatch(updateMeeting(meeting));
        }
        if (notification_type === 'meeting_cancelled' && meeting?.id) {
          dispatch(removeMeeting(meeting.id));
        }
      }
    } catch (parseError) {
      console.error('Error parsing WebSocket message:', parseError, 'Raw message:', event.data);
    }
  }, [dispatch, normalizeNotification, validateNotificationMessage]);

  // Handle WebSocket connection open
  const handleOpen = useCallback(() => {
    console.log('🔗 WebSocket connected');
    console.log('📊 Setting connection state to CONNECTED');
    updateConnectionState(WS_STATES.CONNECTED);
    
    // Reset reconnection parameters
    reconnectAttemptsRef.current = 0;
    reconnectDelayRef.current = RECONNECT_CONFIG.INITIAL_DELAY;
    
    // Clear any existing reconnection timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    // Reset toast flags on successful connection
    resetToastFlags();
    
    // No toast notification for connection success
  }, [updateConnectionState, resetToastFlags]);

  // Handle WebSocket connection close
  const handleClose = useCallback((event) => {
    console.log('⚠️ onclose', event.code, event.reason);
    console.log('🔌 WebSocket disconnected:', event.code, event.reason);
    updateConnectionState(WS_STATES.DISCONNECTED);
    
    // Only attempt reconnection if not manually closed and component is mounted
    if (event.code !== 1000 && mountedRef.current && isOnline) {
      scheduleReconnect();
    }
  }, [updateConnectionState, isOnline]);

  // Handle WebSocket errors
  const handleError = useCallback((error) => {
    console.error('🚨 WebSocket error:', error);
    updateConnectionState(WS_STATES.ERROR);
    
    // Only show error toast once and only if we have multiple consecutive errors
    if (reconnectAttemptsRef.current >= 2 && !hasShownConnectionErrorRef.current) {
      hasShownConnectionErrorRef.current = true;
      toast.error('Connection issues. Notifications may be delayed.', {
        id: 'connection-issues-toast',
        duration: 3000,
        position: 'top-right',
      });
    }
  }, [updateConnectionState]);

  // Schedule reconnection with exponential backoff
  const scheduleReconnect = useCallback(() => {
    if (reconnectAttemptsRef.current >= RECONNECT_CONFIG.MAX_RETRIES) {
      console.error('Max reconnection attempts reached');
      updateConnectionState(WS_STATES.ERROR);
      
      // Only show final error toast once
      if (!hasShownMaxRetriesErrorRef.current) {
        hasShownMaxRetriesErrorRef.current = true;
        toast.error('Unable to connect to notifications. Check your connection.', {
          id: 'max-retries-toast',
          duration: 5000,
          position: 'top-right',
        });
      }
      return;
    }

    updateConnectionState(WS_STATES.RECONNECTING);
    
    const delay = Math.min(
      reconnectDelayRef.current * Math.pow(RECONNECT_CONFIG.BACKOFF_FACTOR, reconnectAttemptsRef.current),
      RECONNECT_CONFIG.MAX_DELAY
    );
    
    console.log(`🔄 Scheduling reconnect attempt ${reconnectAttemptsRef.current + 1} in ${delay}ms`);
    
    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectAttemptsRef.current++;
      connect();
    }, delay);
  }, [updateConnectionState]);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!tokens?.access) {
      console.warn('No JWT token available for WebSocket connection');
      return;
    }

    // Guard against duplicate connections
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected, skipping duplicate connection attempt');
      return;
    }

    // Also check for connecting state to prevent race conditions
    if (wsRef.current && wsRef.current.readyState === WebSocket.CONNECTING) {
      console.log('WebSocket already connecting, skipping duplicate connection attempt');
      return;
    }

    try {
      updateConnectionState(WS_STATES.CONNECTING);
      console.log('🔌 Connecting to WebSocket:', wsUrl);
      
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = handleOpen;
      wsRef.current.onmessage = handleMessage;
      wsRef.current.onclose = handleClose;
      wsRef.current.onerror = handleError;
      
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      updateConnectionState(WS_STATES.ERROR);
    }
  }, [tokens?.access, wsUrl, handleOpen, handleMessage, handleClose, handleError, updateConnectionState]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    console.log('🔌 Manually disconnecting WebSocket');
    
    // Clear reconnection timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    // Close WebSocket connection
    if (wsRef.current) {
      wsRef.current.close(1000, 'Manual disconnect');
      wsRef.current = null;
    }
    
    updateConnectionState(WS_STATES.DISCONNECTED);
  }, [updateConnectionState]);

  // Manual reconnect function
  const reconnect = useCallback(() => {
    disconnect();
    setTimeout(() => {
      reconnectAttemptsRef.current = 0;
      reconnectDelayRef.current = RECONNECT_CONFIG.INITIAL_DELAY;
      // Reset toast flags when manually reconnecting
      resetToastFlags();
      connect();
    }, 100);
  }, [disconnect, connect, resetToastFlags]);

  // Handle online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (connectionState === WS_STATES.DISCONNECTED) {
        console.log('🌐 Back online, reconnecting...');
        reconnect();
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      console.log('🌐 Gone offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [connectionState, reconnect]);

  // Auto-connect disabled - connection is now managed by WebSocketManager
  // useEffect(() => {
  //   if (tokens?.access && (connectionState === WS_STATES.DISCONNECTED || connectionState === WS_STATES.ERROR)) {
  //     connect();
  //   }
  // }, [tokens?.access, connectionState, connect]);

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      console.log("🧹 Cleaning up WebSocket connection on unmount");
      disconnect();
    };
  }, [disconnect]);

  return {
    connectionState,
    isOnline,
    connect,
    disconnect,
    reconnect,
    isConnected: connectionState === WS_STATES.CONNECTED,
    isConnecting: connectionState === WS_STATES.CONNECTING,
    isReconnecting: connectionState === WS_STATES.RECONNECTING,
    hasError: connectionState === WS_STATES.ERROR
  };
};

export default useWebSocket; 